import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { PackageCheck, Plus, Search } from 'lucide-react'
import { listPOs } from '../../api/purchase'
import Spinner from '../../components/Spinner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { fmtDay, parseWallClock } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import PurchaseTabs from './PurchaseTabs'
import { poState } from './purchaseShared'
import { ItemForToggle, setParam, useItemFor } from './itemFor'

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'approval', label: 'Waiting for approval' },
  { id: 'ordered', label: 'Waiting for goods' },
  { id: 'received', label: 'Received' },
  { id: 'closed', label: 'Closed' },
  { id: 'rejected', label: 'Rejected' },
]

export default function POList() {
  const nav = useNavigate()
  const [params, setParams] = useSearchParams()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const filter = FILTERS.some((f) => f.id === params.get('show')) ? params.get('show') : 'all'
  const setFilter = (id) => setParam(setParams, 'show', id)
  const itemFor = useItemFor()

  useEffect(() => {
    listPOs()
      .then((data) => setRows(Array.isArray(data) ? data : (data?.rows || [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [])

  // With Production: for resale, for materials, or all.
  const mine = useMemo(() => rows.filter(itemFor.matches), [rows, itemFor.value]) // eslint-disable-line react-hooks/exhaustive-deps
  const counts = useMemo(() => Object.fromEntries(
    FILTERS.map((f) => [f.id, mine.filter((r) => f.id === 'all' || poState(r).id === f.id).length])
  ), [mine])

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return mine
      .filter((r) => (filter === 'all' || poState(r).id === filter)
        && (!term || [docNo('po', r.po_no), r.vendor_name].join(' ').toLowerCase().includes(term)))
      .sort((a, b) => (parseWallClock(b.po_date)?.getTime() || 0) - (parseWallClock(a.po_date)?.getTime() || 0) || num(b.po_id) - num(a.po_id))
  }, [mine, filter, search])

  return (
    <div className="ci-page">
      <PurchaseTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Purchase orders</h1>
            <div className="ci-title-meta">What you’ve ordered from vendors, and whether it has arrived.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <Link className="ci-btn ci-btn-primary" to={`/purchase/po-form/new${itemFor.query}`}><Plus size={15} />New purchase order</Link>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search orders">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="PO number or vendor…" />
        </label>
        <ItemForToggle itemFor={itemFor} rows={rows} />
      </div>

      <FilterChips options={FILTERS.map((f) => ({ ...f, count: counts[f.id] }))} value={filter} onChange={setFilter} />

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Vendor</th>
                <th>Ordered</th>
                <th className="center">Status</th>
                <th className="num">Total</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6"><Spinner label="Loading orders…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="6"><div className="ci-empty">{rows.length ? 'No orders match.' : 'No purchase orders yet.'}</div></td></tr>
              ) : shown.map((r) => {
                const st = poState(r)
                return (
                  <tr key={r.po_id} className="row-link" onClick={() => nav(`/purchase/po-detail/${r.po_id}`)}>
                    <td><Link className="ci-je-link" to={`/purchase/po-detail/${r.po_id}`} onClick={(e) => e.stopPropagation()}>{docNo('po', r.po_no)}</Link></td>
                    <td className="ci-strong">{r.vendor_name || '–'}</td>
                    <td className="ci-muted-cell">{fmtDay(r.po_date) || '–'}</td>
                    <td className="center"><StatusBadge label={st.label} tone={st.tone} /></td>
                    <td className="num ci-total">{money(r.total_amount)}</td>
                    <td className="num" onClick={(e) => e.stopPropagation()}>
                      {st.id === 'ordered' && (
                        <Link className="ci-btn ci-btn-ghost ci-btn-sm" to={`/purchase/receipts/new?po_id=${r.po_id}`}><PackageCheck size={13} />Receive</Link>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{shown.length} {shown.length === 1 ? 'order' : 'orders'} · {money(shown.reduce((s, r) => s + num(r.total_amount), 0))}</span>
        </div>
      </div>
    </div>
  )
}
